import { css } from "@emotion/react";
import { AnimatePresence, motion } from "framer-motion";
import { rem } from "polished";
import React, { useEffect } from "react";
import { useTranslation } from "react-i18next";
import {
  selector,
  useRecoilValue,
  useRecoilValueLoadable,
  useSetRecoilState,
} from "recoil";

import { pendingEthTxAtom } from "../atoms";
import { Flex } from "../base";
import { getEtherscanLink } from "../lib/ethereum";
import { removeItemAtIndex, useChainId } from "../modules/hooks";
import { isLoadingSelector } from "./BillBoard";
import { LoadingOrb } from "./LoadingBlock";
import Notification from "./Notification";

const pendingTxSelector = selector({
  key: "pendingTransactions",
  get: ({ get }) => {
    const pendingTransactions = get(pendingEthTxAtom);
    return pendingTransactions;
  },
});

const PendingTxNotification = ({
  title,
  hash,
  confirmations,
  requiredConfirmations,
}) => {
  const chainId = useChainId();

  const setPendingEthTx = useSetRecoilState(pendingEthTxAtom);

  useEffect(() => {
    let cb;
    if (confirmations === requiredConfirmations) {
      cb = setTimeout(() => {
        setPendingEthTx((state) => {
          const idx = state.findIndex((pendingTx) => pendingTx.hash === hash);
          if (idx < 0) {
            return state;
          }
          return removeItemAtIndex(state, idx);
        });
      }, 3000);
    }

    return () => {
      if (cb) {
        clearTimeout(cb);
      }
    };
  }, [hash, confirmations, requiredConfirmations, setPendingEthTx]);

  return (
    <a
      href={getEtherscanLink(chainId, hash, "transaction")}
      rel="noopener noreferrer"
      target="_blank"
    >
      <Notification
        icon={
          <LoadingOrb
            done={confirmations === requiredConfirmations}
            size={40}
          />
        }
        subtitle={`${confirmations ?? ""} of ${
          requiredConfirmations ?? ""
        } confirmations`}
        title={title}
      />
    </a>
  );
};

const NotificationHUD = () => {
  const { t } = useTranslation();
  const { state } = useRecoilValueLoadable(isLoadingSelector);

  const isLoading = state === "loading";

  const pendingTxs = useRecoilValue(pendingTxSelector);

  return (
    <div
      css={css`
        position: fixed;
        z-index: 101;
        left: 0;
        right: 0;
        bottom: ${rem(32)};

        display: flex;
        flex-direction: column;
        align-items: center;
      `}
    >
      <AnimatePresence>
        {pendingTxs?.map((pendingTx) => {
          let title = t("notifications.submittingTx");
          switch (pendingTx.watchKey) {
            case "swap": {
              title = t("notifications.swap");
              break;
            }
            case "allow": {
              title = t("notifications.allow");
              break;
            }
            case "supply": {
              title = t("notifications.supply");
              break;
            }
            case "move": {
              title = t("notifications.move");
              break;
            }
            case "release": {
              title = t("notifications.release");
              break;
            }
          }

          return (
            <motion.div
              key={pendingTx.hash}
              animate={{ opacity: 1, y: 0 }}
              exit={{ opacity: 0, y: 90 }}
              initial={{ opacity: 0, y: 30 }}
            >
              <PendingTxNotification
                confirmations={pendingTx.confirmations}
                hash={pendingTx.hash}
                requiredConfirmations={pendingTx.requiredConfirmations}
                title={title}
              />
            </motion.div>
          );
        }) ?? null}
        {isLoading ? (
          <motion.div
            key="loading"
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: 90 }}
            initial={{ opacity: 0, y: 30 }}
          >
            <Notification
              icon={
                <Flex center>
                  <LoadingOrb size={40} />
                </Flex>
              }
              subtitle={t("notifications.pleaseWait")}
              title={t("notifications.loading")}
            ></Notification>
          </motion.div>
        ) : null}
      </AnimatePresence>
    </div>
  );
};

export default NotificationHUD;
